import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button.js';
import { IconButton } from '../../ui/IconButton.js';
import { Icon } from '../../Icon.js';
import { CommentsSection } from './CommentsSection.js';
import { InspectorSection } from './InspectorSection.js';
import { PendingChangesSection } from './PendingChangesSection.js';
import type { PageEditorState } from './usePageEditorState.js';

// ── Controles del editor, dentro del panel de Evo Brain ──────────────────────
//
// Van acá y no sobre la vista previa porque el panel es donde se trabaja: se
// alterna entre pedirle algo a Evo Brain y tocar la página a mano sin cambiar de
// lugar en la pantalla.
//
// Se monta en el slot `topContent` de AssistantModuleConfig, que el shell fija
// arriba del panel (no scrollea con la conversación). El slot ya existía; no
// hizo falta mecanismo nuevo.
//
// Estructura pensada para lo que viene: una fila de modo, y debajo el cuerpo
// del modo activo. Cuando lleguen el inspector de propiedades y los comentarios,
// son modos nuevos en la misma fila y cuerpos nuevos en el mismo lugar — no
// controles sueltos en otra parte de la pantalla.

export interface PageEditorPanelProps {
  editor: PageEditorState;
}

/** Aviso cuando la página no se puede editar, con el motivo y la salida. */
function BlockerNotice({ blocker }: { blocker: NonNullable<PageEditorState['blocker']> }) {
  const { t } = useTranslation();
  return (
    <div style={{
      padding: 'var(--evo-space-3)',
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r-sm)',
      background: 'var(--evo-bg-2)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--evo-space-2)',
    }}>
      <span style={{
        fontSize: 'var(--evo-fs-label)',
        color: 'var(--evo-fg-2)',
        fontFamily: 'var(--evo-font)',
        fontWeight: 'var(--evo-fw-medium)',
      }}>
        {t(`projects.editor.blocker.${blocker}.title`)}
      </span>
      <span style={{
        fontSize: 'var(--evo-fs-xs)',
        color: 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)',
        lineHeight: 1.5,
      }}>
        {t(`projects.editor.blocker.${blocker}.hint`)}
      </span>
    </div>
  );
}

export function PageEditorPanel({ editor }: PageEditorPanelProps) {
  const { t } = useTranslation();
  const { mode, blocker, pendingCount, isSaving, save, discard, selection, editableCount } = editor;
  const { undo, redo, canUndo, canRedo } = editor;

  // Cmd+Z / Cmd+Shift+Z (Ctrl en Windows). Es lo primero que la mano intenta en
  // un panel que se parece a un programa de diseño, y hasta ahora no hacía nada.
  //
  // Se escucha en `window` y no en el panel porque cuando estás editando el
  // foco vive DENTRO del iframe de la página, no en el panel: un handler
  // colgado del panel no se enteraría nunca.
  //
  // No se pisa el deshacer del navegador dentro de un campo de texto: si el
  // foco está en un input o textarea del panel, el atajo se deja pasar.
  useEffect(() => {
    if (mode === 'view') return;
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const activo = document.activeElement;
      const tag = activo?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.shiftKey) {
        if (!canRedo) return;
        e.preventDefault();
        redo();
      } else {
        if (!canUndo) return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [mode, undo, redo, canUndo, canRedo]);

  // Mirando una versión vieja no hay nada que ofrecer: ni siquiera el selector.
  if (blocker === 'viewing-old') {
    return (
      <div style={{ marginBottom: 'var(--evo-space-3)' }}>
        <BlockerNotice blocker="viewing-old" />
      </div>
    );
  }

  const hasChanges = pendingCount > 0;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--evo-space-3)',
      marginBottom: 'var(--evo-space-3)',
    }}>
      {/* El selector de ancho vivía acá y se fue (2026-08-31): el visor de la
          página ya tiene esos tres iconos arriba a la izquierda, y repetirlos
          en el panel obliga a mirar dos lugares para saber en qué ancho estás.
          El estado sigue en usePageEditorState — lo que se quitó es el control
          duplicado, no la función. */}

      {/* Cabecera del panel: a la izquierda dónde estás parado, a la derecha
          deshacer/rehacer. Los dos existían en el estado desde el principio y
          solo se veían al fondo, dentro de la lista de cambios pendientes — o
          sea recién DESPUÉS de romper algo. Un deshacer que aparece cuando ya
          lo necesitás llega tarde: acá está fijo, como en cualquier programa
          de diseño, y responde a Cmd+Z / Cmd+Shift+Z. */}
      {mode !== 'view' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)' }}>
          {/* Camino de ancestros: subir al contenedor sin tener que acertarle.
              Comentando importa MÁS que editando: lo que se marca para pedir un
              cambio suele ser el contenedor (una sección de tres columnas), y
              acertarle al padding de un grid con el mouse no es razonable. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px', minWidth: 0 }}>
            {editor.ancestors.length > 1 && editor.ancestors.map((a, i) => (
              <span key={a.targetId} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                {i > 0 && (
                  <span style={{ fontSize: 'var(--evo-fs-xs)', color: 'var(--evo-fg-3)' }}>›</span>
                )}
                <Button
                  variant={i === editor.ancestors.length - 1 ? 'outline' : 'ghost'}
                  tone={i === editor.ancestors.length - 1 ? 'cyan' : 'neutral'}
                  size="sm"
                  onMouseDown={e => { e.preventDefault(); }}
                  onClick={() => { editor.selectAncestor(a.targetId); }}
                >
                  {a.tag}
                </Button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <IconButton
              icon={<Icon name="undo" size={15} />}
              ariaLabel={t('projects.editor.changes.undo')}
              variant="ghost" tone="muted" size="sm"
              disabled={!editor.canUndo}
              onMouseDown={e => { e.preventDefault(); }}
              onClick={editor.undo}
            />
            <IconButton
              icon={<Icon name="redo" size={15} />}
              ariaLabel={t('projects.editor.changes.redo')}
              variant="ghost" tone="muted" size="sm"
              disabled={!editor.canRedo}
              onMouseDown={e => { e.preventDefault(); }}
              onClick={editor.redo}
            />
          </div>
        </div>
      )}

      {/* Comentar: marcar y pedir un cambio, en vez de tocar la página */}
      {mode === 'comment' && (
        blocker === 'not-stamped'
          ? <BlockerNotice blocker="not-stamped" />
          : <CommentsSection editor={editor} />
      )}

      {/* Cuerpo del modo activo */}
      {mode === 'edit' && (
        blocker === 'not-stamped' ? (
          <BlockerNotice blocker="not-stamped" />
        ) : (
          <>
            {/* Qué hay para editar en esta página. Un número concreto vale más
                que "haz clic en un texto": si dice 0, ya se sabe que el
                problema no es dónde clickeaste. */}
            <span style={{
              fontSize: 'var(--evo-fs-xs)',
              color: 'var(--evo-fg-3)',
              fontFamily: 'var(--evo-font)',
              lineHeight: 1.5,
            }}>
              {editableCount === null
                ? t('projects.editor.hint')
                : t('projects.editor.hintWithCount', { count: editableCount })}
            </span>

            {/* Lo seleccionado: si no se puede tocar, se dice por qué. */}
            {selection && !selection.editable && selection.reason && (
              <div style={{
                padding: 'var(--evo-space-3)',
                border: '1px dashed var(--evo-stroke)',
                borderRadius: 'var(--evo-r-sm)',
                background: 'var(--evo-bg-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}>
                <span style={{
                  fontSize: 'var(--evo-fs-xs)',
                  color: 'var(--evo-fg-2)',
                  fontFamily: 'var(--evo-font)',
                  fontWeight: 'var(--evo-fw-medium)',
                }}>
                  {t('projects.editor.locked.title', { tag: selection.tag })}
                </span>
                <span style={{
                  fontSize: 'var(--evo-fs-xs)',
                  color: 'var(--evo-fg-3)',
                  fontFamily: 'var(--evo-font)',
                  lineHeight: 1.5,
                }}>
                  {t(`projects.editor.locked.${selection.reason}`)}
                </span>
              </div>
            )}
            {/* Formato e inspector del elemento seleccionado */}
            <InspectorSection editor={editor} />

            <PendingChangesSection editor={editor} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', justifyContent: 'flex-end' }}>
              <Button
                variant="ghost"
                tone="neutral"
                size="sm"
                disabled={!hasChanges || isSaving}
                onClick={discard}
              >
                {t('projects.editor.discard')}
              </Button>
              <Button
                variant="solid"
                tone="cyan"
                size="sm"
                disabled={!hasChanges}
                loading={isSaving}
                onClick={save}
              >
                {t('projects.editor.save')}
              </Button>
            </div>
          </>
        )
      )}
    </div>
  );
}
